import { TplMgr } from "@/wab/shared/TplMgr";
import { ensure, mkShortId } from "@/wab/shared/common";
import { allComponentVariants } from "@/wab/shared/core/components";
import { Pt } from "@/wab/shared/geom";
import { Arena, ArenaFrame, Component, Site } from "@/wab/shared/model/classes";
import { mmToCssPx } from "./book";
import { resolvePageBoxes } from "./composition";
import { PUBLISHING_MASTER_VIEW_KEY, PublishingMasterView, publishingMasterViewSchema, readPublishingDocument } from "./document";
import { resolveMasterDefinition } from "./masters";

export function readPublishingMasterView(site: Pick<Site, "flags">): PublishingMasterView | undefined {
  const raw = site.flags[PUBLISHING_MASTER_VIEW_KEY];
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw !== "string") throw new Error("Invalid master authoring view identity");
  return publishingMasterViewSchema.parse(JSON.parse(raw));
}
export function isPublishingMasterArena(site: Site, arena: Arena) {
  return arena.publishingViewId === readPublishingMasterView(site)?.id && !!arena.publishingViewId;
}

/** All positions are derived from master definitions and physical geometry.
 * Saved frame positions are disposable editing adapters, never publishing data.
 * The right sheet is placed so its TRIM box starts exactly where the left TRIM
 * box ends, whatever each side's bleed/slug is; bleed and slug overlap the
 * neighbour. `rowGap` separates master rows (a function receives the master
 * whose row follows the gap). */
export function getPublishingMasterLayout(site: Site, arena: Arena, rowGap: number | ((masterId: string, index: number) => number) = 80, topInset = 0, selection?: { selectedMasterId?: string }) {
  const doc = ensure(readPublishingDocument(site), "Initialize publishing first");
  const view = ensure(readPublishingMasterView(site), "Master authoring view is not initialized");
  if (arena.publishingViewId !== view.id) throw new Error("Not the managed master authoring view");
  let y = topInset;
  const rows: { masterId: string; left: number; top: number; width: number; height: number }[] = [];
  const selectedMasterId = selection ? selection.selectedMasterId : view.selectedMasterId;
  const sheets = (doc.masters ?? []).filter((master) => !selectedMasterId || master.id === selectedMasterId).flatMap((master, index) => {
    const resolved = resolveMasterDefinition(doc, master.id);
    const trimWidth = mmToCssPx(resolved.geometry.trim.width);
    const leftTrimX = mmToCssPx(resolvePageBoxes(resolved.geometry, "left").trim.x);
    if (index > 0) y += typeof rowGap === "function" ? rowGap(master.id, index) : rowGap;
    const row = (["left", "right"] as const).map((side) => {
      const boxes = resolvePageBoxes(resolved.geometry, side);
      const mediaPosition = { x: side === "left" ? 0 : leftTrimX + trimWidth - mmToCssPx(boxes.trim.x), y };
      const items = resolved.items.filter(({ item }) => item.side === "both" || item.side === side).map((entry) => {
        const frameId = view.frames[master.id]?.[entry.item.id]?.[side];
        const frame = ensure(arena.children.find((f) => f.uuid === frameId), `Master ${master.id}/${entry.item.id}/${side} has no authoring frame`);
        return { ...entry, frame, position: { x: mediaPosition.x + mmToCssPx(boxes.trim.x + entry.item.position.x), y: mediaPosition.y + mmToCssPx(boxes.trim.y + entry.item.position.y) } };
      });
      return { master, side, geometry: resolved.geometry, boxes, mediaPosition, items };
    });
    const left = Math.min(...row.map((s) => s.mediaPosition.x));
    const height = Math.max(...row.map((s) => mmToCssPx(s.boxes.media.height)));
    rows.push({ masterId: master.id, left, top: y, width: Math.max(...row.map((s) => s.mediaPosition.x + mmToCssPx(s.boxes.media.width))) - left, height });
    y += height;
    return row;
  });
  return { sheets, rows, width: Math.max(0, ...sheets.map((s) => s.mediaPosition.x + mmToCssPx(s.boxes.media.width))), height: sheets.length ? y : 0 };
}

/** Caller records one native transaction. Retire owned adapters only. */
export function reconcilePublishingMasterView(site: Site) {
  const doc = ensure(readPublishingDocument(site), "Initialize publishing first");
  const view = readPublishingMasterView(site) ?? { id: mkShortId(), frames: {} };
  const matches = site.arenas.filter((arena) => arena.publishingViewId === view.id);
  if (matches.length > 1) throw new Error("Duplicated master authoring view identity");
  const mgr = new TplMgr({ site });
  const arena = matches[0] ?? mgr.addArena("Master pages");
  arena.publishingViewId = view.id;
  const frames: PublishingMasterView["frames"] = Object.create(null);
  for (const master of doc.masters ?? []) {
    const resolved = resolveMasterDefinition(doc, master.id);
    const items: PublishingMasterView["frames"][string] = Object.create(null);
    for (const { item } of resolved.items) {
      const component = ensure(site.components.find((c) => c.uuid === item.componentId), "Master source component does not exist");
      const sides: { left?: string; right?: string } = {};
      for (const side of ["left", "right"] as const) {
        if (item.side !== "both" && item.side !== side) continue;
        let frame = arena.children.find((f) => f.uuid === view.frames[master.id]?.[item.id]?.[side]);
        if (frame?.container.component !== component) frame = undefined;
        frame ??= mgr.addNewMixedArenaFrame(arena, component.name, component, { width: mmToCssPx(resolved.geometry.trim.width), height: mmToCssPx(resolved.geometry.trim.height), insertPt: new Pt(0, 0) });
        frame.width = mmToCssPx(resolved.geometry.trim.width);
        frame.height = mmToCssPx(resolved.geometry.trim.height);
        frame.pinnedVariants = Object.fromEntries(item.printState.variantIds.map((id) => [id, true]));
        frame.pinnedGlobalVariants = Object.fromEntries(item.printState.globalVariantIds.map((id) => [id, true]));
        frame.targetVariants = allComponentVariants(component).filter((v) => item.printState.variantIds.includes(v.uuid));
        frame.targetGlobalVariants = site.globalVariantGroups.flatMap((g) => g.variants).filter((v) => item.printState.globalVariantIds.includes(v.uuid));
        sides[side] = frame.uuid;
      }
      items[item.id] = sides;
    }
    frames[master.id] = items;
  }
  const ids = (input: PublishingMasterView["frames"]) => Object.values(input).flatMap((items) => Object.values(items).flatMap((sides) => Object.values(sides)));
  const previous = new Set(ids(view.frames)), retained = new Set(ids(frames));
  arena.children = arena.children.filter((frame) => !previous.has(frame.uuid) || retained.has(frame.uuid));
  site.flags[PUBLISHING_MASTER_VIEW_KEY] = JSON.stringify(publishingMasterViewSchema.parse({ id: view.id, frames, selectedMasterId: doc.masters?.some((master) => master.id === view.selectedMasterId) ? view.selectedMasterId : undefined }));
  const layout = getPublishingMasterLayout(site, arena);
  for (const sheet of layout.sheets) for (const item of sheet.items) {
    item.frame.left = item.position.x;
    item.frame.top = item.position.y;
  }
  return { arena, layout };
}

/** Editing-view selection only; caller records the native transaction. */
export function selectPublishingMasterView(site: Site, masterId?: string) {
  const doc = ensure(readPublishingDocument(site), "Initialize publishing first");
  if (masterId !== undefined) {
    ensure(doc.masters?.find((master) => master.id === masterId), "Master does not exist");
  } else {
    const selected = readPublishingMasterView(site)?.selectedMasterId;
    masterId = doc.masters?.find((master) => master.id === selected)?.id ?? doc.masters?.[0]?.id;
  }
  reconcilePublishingMasterView(site);
  const view = ensure(readPublishingMasterView(site), "Master authoring view is not initialized");
  site.flags[PUBLISHING_MASTER_VIEW_KEY] = JSON.stringify(publishingMasterViewSchema.parse({ ...view, selectedMasterId: masterId }));
  return reconcilePublishingMasterView(site);
}

type MasterLayout = ReturnType<typeof getPublishingMasterLayout>;
type MasterSheet = MasterLayout["sheets"][number];

/** The one native editing authority for master layers (project, save, block and
 * per-component permission). Canvas rendering and entry focus both use it. */
export function publishingMasterEditAuthority(ctx: { canEditProject(): boolean; canSave(): boolean; blockChanges: boolean; canEditComponent(component: Component): boolean }) {
  return (component: Component) => ctx.canEditProject() && ctx.canSave() && !ctx.blockChanges && ctx.canEditComponent(component);
}

/** Layers of a sheet that native editing may focus. Inherited layers and the
 * right-hand mirror of a two-sided item are display-only. `canEdit` carries the
 * caller's native permission authority. */
export function listPublishingMasterEditableEntries(sheet: MasterSheet, canEdit: (component: Component) => boolean) {
  return sheet.items.filter((entry) => !entry.inherited && !(entry.item.side === "both" && sheet.side === "right") && canEdit(entry.frame.container.component));
}

/** The frame native focus belongs on when the Master pages view is entered: the
 * first editable layer of the selected master, never the arena's first child
 * (which can belong to a master the view does not display). */
export function getPublishingMasterEntryFrame(site: Site, arena: Arena, canEdit: (component: Component) => boolean, selection?: { selectedMasterId?: string }): ArenaFrame | undefined {
  for (const sheet of getPublishingMasterLayout(site, arena, undefined, undefined, selection).sheets) {
    const [entry] = listPublishingMasterEditableEntries(sheet, canEdit);
    if (entry) return entry.frame;
  }
  return undefined;
}

/** False only for a frame owned by a master the view currently filters out; such
 * a frame has no mounted canvas, so no ViewCtx can ever appear for it. */
export function isPublishingMasterFrameDisplayed(site: Site, arena: Arena, frame: ArenaFrame, selection?: { selectedMasterId?: string }) {
  if (!isPublishingMasterArena(site, arena)) return true;
  const view = readPublishingMasterView(site);
  if (!view) return true;
  const selectedMasterId = selection ? selection.selectedMasterId : view?.selectedMasterId;
  if (!selectedMasterId) return true;
  const ownedBy = (masterId: string) => Object.values(view.frames[masterId] ?? {}).some((sides) => Object.values(sides).includes(frame.uuid));
  if (ownedBy(selectedMasterId)) return true;
  return !Object.keys(view.frames).some(ownedBy);
}
